import { readoutHoverLines } from "../lib/fertility";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as maplibregl from "maplibre-gl";
import type { Map as MlMap, MapMouseEvent } from "maplibre-gl";
import { Map, type ErrorEvent, type MapEvent } from "@vis.gl/react-maplibre";
import { ensureMapWorker, MapGpuNotice } from "../lib/createMap";
import { loseWebglContextAfterRemove } from "../lib/webglRelease";
import "maplibre-gl/dist/maplibre-gl.css";
import { Loader2 } from "lucide-react";
import type {
  DashboardField,
  ManifestLayer,
  SoilSamplePoint,
} from "../types/contract";
import { useFieldLayerList } from "../hooks/queries";
import { choroplethFillColor } from "../lib/cogColor";
import { bboxOfGeoJSON, isFittableBbox } from "../lib/geo";

// The binding constructs MapLibre itself. Same worker chunk createMap registers.
ensureMapWorker();

// One object: a new identity makes the binding call setStyle and drop the Rx layers.
const PREVIEW_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    sat: {
      type: "raster",
      tiles: [
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      attribution: "© Esri",
    },
  },
  layers: [{ id: "sat", type: "raster", source: "sat" }],
};

function previewCenter(field: DashboardField): {
  longitude: number;
  latitude: number;
} {
  if (isFittableBbox(field.bbox)) {
    return {
      longitude: (field.bbox[0] + field.bbox[2]) / 2,
      latitude: (field.bbox[1] + field.bbox[3]) / 2,
    };
  }
  const center = field.centroid ?? [-98, 39];
  return { longitude: center[0], latitude: center[1] };
}

// AutoRx preview: a static, hover-only mini-map of a preset's default Rx surface — the per-cell
// fert choropleth (colored like the live map via choroplethFillColor) with the field's REAL
// sampling points ON TOP.

export function BuildRxPreviewMap({
  field,
  layer,
  samples,
  loading,
  error,
}: {
  field: DashboardField;
  layer: ManifestLayer | null;
  samples: SoilSamplePoint[];
  loading: boolean;
  error: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const [gpuMissing, setGpuMissing] = useState(false);
  const [ready, setReady] = useState(false);
  const [hover, setHover] = useState<{
    rate: string;
    lines: { name: string; text: string }[];
  } | null>(null);
  const fittedRef = useRef(false);

  const { data: listed } = useFieldLayerList(field.field_id);
  const boundaryGeom = listed?.document.boundary as
    GeoJSON.GeoJSON | null | undefined;

  const geo = useMemo(
    () => (boundaryGeom ? { boundaryGeom } : undefined),
    [boundaryGeom],
  );

  const samplesFc = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: "FeatureCollection",
      features: samples
        .filter((s) => Number.isFinite(s.lon) && Number.isFinite(s.lat))
        .map((s) => ({
          type: "Feature",
          properties: {},
          geometry: { type: "Point", coordinates: [s.lon, s.lat] },
        })),
    }),
    [samples],
  );

  const unit = layer?.unit ?? "";
  const decimals = unit === "t/ac" ? 2 : 0;

  // The map is created once. The center is whatever bbox the first render had.
  const fieldRef = useRef(field);
  const [initialView] = useState(() => previewCenter(field));
  useLayoutEffect(() => {
    fieldRef.current = field;
  }, [field]);

  const onLoad = useCallback((event: MapEvent) => {
    const map = event.target;
    mapRef.current = map;
    loseWebglContextAfterRemove(map);
    map.resize();
    setReady(true);
  }, []);

  const onError = useCallback((event: ErrorEvent) => {
    if (event.error instanceof maplibregl.GPUInitializationError) {
      setGpuMissing(true);
      return;
    }
    console.error(event.error);
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const el = containerRef.current;
    if (!map || !ready || !el) return;
    const ro = new ResizeObserver(() => {
      try {
        map.resize();
      } catch {
        /* removed */
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [ready]);

  // add the Rx choropleth + boundary + sample overlay + hover
  useEffect(() => {
    const field = fieldRef.current;
    const map = mapRef.current;
    if (!map || !ready) return;
    const setSrc = (id: string, data: GeoJSON.GeoJSON) => {
      const src = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
      if (src) src.setData(data);
      else map.addSource(id, { type: "geojson", data });
    };
    const addLayer = (def: maplibregl.LayerSpecification) => {
      if (!map.getLayer(def.id)) map.addLayer(def);
    };

    if (!fittedRef.current) {
      const bb =
        bboxOfGeoJSON(geo?.boundaryGeom) ??
        (isFittableBbox(field.bbox) ? field.bbox : null);
      if (isFittableBbox(bb)) {
        try {
          map.fitBounds(
            [
              [bb[0], bb[1]],
              [bb[2], bb[3]],
            ],
            { padding: 18, duration: 0, maxZoom: 16 },
          );
          fittedRef.current = true;
        } catch {
          /* bad bbox */
        }
      }
    }

    // 1. the Rx choropleth (underlay) — colored exactly like the live fert-* layer. When the layer
    // changes (e.g. served→computed, re-map) the color scale bakes THIS layer's value_min/max, so keep
    // the paint in sync — addLayer short-circuits once rx-fill exists, so update the paint explicitly.
    if (layer?.inline) {
      setSrc("rx-fill", layer.inline as GeoJSON.GeoJSON);
      const fillColor = choroplethFillColor(
        layer,
      ) as unknown as maplibregl.ExpressionSpecification;
      if (map.getLayer("rx-fill"))
        map.setPaintProperty("rx-fill", "fill-color", fillColor);
      else
        addLayer({
          id: "rx-fill",
          type: "fill",
          source: "rx-fill",
          paint: { "fill-color": fillColor, "fill-opacity": 0.82 },
        });
    }
    // 2. boundary
    if (geo?.boundaryGeom) {
      setSrc("rx-boundary", geo.boundaryGeom);
      addLayer({
        id: "rx-boundary-casing",
        type: "line",
        source: "rx-boundary",
        paint: {
          "line-color": "#000000",
          "line-width": 3,
          "line-opacity": 0.5,
        },
      });
      addLayer({
        id: "rx-boundary-line",
        type: "line",
        source: "rx-boundary",
        paint: { "line-color": "#ffffff", "line-width": 1.4 },
      });
    }
    // 3. the real sampling POINTS on top
    if (samplesFc.features.length) {
      setSrc("rx-samples", samplesFc);
      addLayer({
        id: "rx-samples",
        type: "circle",
        source: "rx-samples",
        paint: {
          "circle-radius": 3.5,
          "circle-color": "#E09F3E",
          "circle-stroke-color": "#fff",
          "circle-stroke-width": 1,
        },
      });
    }
    // keep boundary + sample points ABOVE the choropleth regardless of data-arrival order (rx-fill can arrive last)
    for (const id of ["rx-boundary-casing", "rx-boundary-line", "rx-samples"])
      if (map.getLayer(id)) map.moveLayer(id);

    const probe = (e: MapMouseEvent) => {
      const hx = map.getLayer("rx-fill")
        ? map.queryRenderedFeatures(e.point, { layers: ["rx-fill"] })[0]
        : undefined;
      const p = hx?.properties;
      const raw = p?.value;
      const v = typeof raw === "number" ? raw : raw != null ? Number(raw) : NaN;
      if (!p || !Number.isFinite(v)) {
        setHover(null);
        return;
      }
      const lines = readoutHoverLines(p as Record<string, unknown>).map(
        (l) => ({ name: l.title, text: l.text }),
      );
      if (p.breakdown)
        lines.push({ name: "Soil composition", text: String(p.breakdown) });
      setHover({
        rate: `${v.toFixed(decimals)}${unit ? ` ${unit}` : ""}`,
        lines,
      });
    };
    const clear = () => setHover(null);
    map.on("mousemove", probe);
    map.on("mouseout", clear);
    map.getCanvas().style.cursor = "pointer";
    return () => {
      try {
        map.off("mousemove", probe);
        map.off("mouseout", clear);
      } catch {
        // The binding removes the map in its own effect, which runs first.
      }
    };
  }, [ready, layer, geo, samplesFc, unit, decimals]);

  return (
    <div className="relative min-h-0 flex-1">
      {gpuMissing ? (
        <MapGpuNotice className="h-full w-full" />
      ) : (
        <div ref={containerRef} className="h-full w-full">
          <Map
            mapLib={maplibregl}
            mapStyle={PREVIEW_STYLE}
            initialViewState={{ ...initialView, zoom: 12 }}
            attributionControl={false}
            // MapLibre 6 defaults this to 4. Undefined keeps the previous overscale.
            zoomLevelsToOverscale={undefined}
            dragPan={false}
            scrollZoom={false}
            doubleClickZoom={false}
            boxZoom={false}
            dragRotate={false}
            touchZoomRotate={false}
            keyboard={false}
            onLoad={onLoad}
            onError={onError}
          />
        </div>
      )}
      {!ready && !gpuMissing && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-hover/20 text-[11px] text-faint">
          <Loader2 size={12} className="mr-1 animate-spin" /> loading…
        </div>
      )}
      {ready && loading && (
        <div className="pointer-events-none absolute inset-x-0 top-1 z-scrim text-center text-[10px] text-muted">
          <Loader2 size={10} className="inline animate-spin" /> computing Rx…
        </div>
      )}
      {ready && !loading && error && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-3 text-center text-[10px] text-amber-400">
          Rx preview not available yet
        </div>
      )}
      {ready && !loading && !error && !layer && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] text-faint">
          no Rx surface
        </div>
      )}
      {hover && (
        <div className="pointer-events-none absolute bottom-1 left-1 z-scrim max-w-[96%] rounded bg-raised/95 px-2 py-1 text-[10px] shadow-md">
          <div className="mb-0.5 font-semibold text-fg">Rx: {hover.rate}</div>
          {hover.lines.map((l, i) => (
            <div
              key={i}
              className="flex items-baseline justify-between gap-3 text-muted"
            >
              <span>{l.name}</span>
              <span className="tnum text-fg">{l.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
